import { Component } from 'react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';

import apiService from '@services/apiService.ts';
import { fileService } from '@services/fileService.ts';
import { modalService } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';
import { PageHeader } from '@components/PageHeader.tsx';
import { Timestamp } from '@components/Timestamp.tsx';
import { CreateUserDialog } from './dialogs/CreateUserDialog.tsx';
import { EditUserDialog } from './dialogs/EditUserDialog.tsx';
import { StorageOverviewDialog } from './dialogs/StorageOverviewDialog.tsx';
import { OrphanScanDialog } from './dialogs/OrphanScanDialog.tsx';
import { ImgAddPerson } from '@components/Images/ImgAddPerson.tsx';
import { ImgTrash } from '@components/Images/ImgTrash.tsx';
import { ImgEdit } from '@components/Images/ImgEdit.tsx';
import { ImgWarning } from '@components/Images/ImgWarning.tsx';
import { ImgPieChart } from '@components/Images/ImgPieChart.tsx';
import { ImgSearch } from '@components/Images/ImgSearch.tsx';
import { Sidebar } from '@components/Sidebar.tsx';

type UserListItem = {
  id: string;
  login: string;
  displayName: string;
  email: string;
  storageLimit: number;
  usedSpace: number;
  filesCount: number;
  roles: string[];
  lastOnline: string | null;
  picture?: string;
  emailMd5?: string;
  createdAt?: string;
};

type SystemStats = {
  diskTotal: number | null;
  diskFree: number | null;
  diskUsed: number | null;
  appUsed: number;
};

@observer
class Users extends Component {
  @observable _users: UserListItem[] | null = null;
  @observable _error: string | null = null;
  @observable _systemStats: SystemStats | null = null;

  constructor(props: {}) {
    super(props);
    makeObservable(this);
  }

  async componentDidMount() {
    await Promise.all([this._loadUsers(), this._loadSystemStats()]);
    document.getElementsByClassName('ui-loader-container')[0]?.remove();
  }

  private async _loadUsers() {
    try {
      const users = await apiService.get<UserListItem[]>('/api/user/list');
      runInAction(() => {
        this._users = users;
        this._error = null;
      });
    } catch (e) {
      runInAction(() => {
        this._error = 'Failed to load users';
      });
    }
  }

  private async _loadSystemStats() {
    try {
      const stats = await apiService.get<SystemStats>('/api/user/system-stats');
      runInAction(() => { this._systemStats = stats; });
    } catch {
      // non-critical
    }
  }

  render() {
    return (
      <div className="app-container">
        <PageHeader
          breadcrumb={
            <>
              <span>›</span>
              <span>Users</span>
            </>
          }
        />

        <div className="main-content">
          <Sidebar activeItem="users" />

          <main className="content-area">
            <div className="toolbar">
              <div className="toolbar-left">
                <h2 style={{ fontSize: '18px', fontWeight: 600 }}>User Management</h2>
              </div>
              <div className="toolbar-left">
                <button className="btn btn-primary" onClick={this._onShowCreateForm}>
                  <ImgAddPerson /> Create User
                </button>
              </div>
            </div>

            {this._systemStats && this._renderDiskStats()}

            {this._error && (
              <div className="users-error">{this._error}</div>
            )}

            {this._users === null ? (
              <div style={{ color: 'var(--text-secondary)', padding: '20px' }}>Loading users...</div>
            ) : (
              this._renderUserList()
            )}
          </main>
        </div>
      </div>
    );
  }

  private _renderDiskStats() {
    const s = this._systemStats!;
    const freePct = s.diskTotal && s.diskFree != null ? s.diskFree / s.diskTotal : null;
    const isLow = freePct !== null && freePct < 0.1;
    return (
      <div style={{ marginBottom: '16px' }}>
        {isLow && (
          <div className="disk-warning-banner">
              <><ImgWarning /> Low disk space — only {fileService.formatFileSize(s.diskFree!)} free ({Math.round(freePct! * 100)}%)</>
          </div>
        )}
        <div className="admin-grid">
          <div className="stat-card">
            <div className="stat-value">{s.diskTotal != null ? fileService.formatFileSize(s.diskTotal) : '—'}</div>
            <div className="stat-label">Disk Total</div>
          </div>
          <div className={`stat-card${isLow ? ' stat-card-danger' : ''}`}>
            <div className="stat-value">{s.diskFree != null ? fileService.formatFileSize(s.diskFree) : '—'}</div>
            <div className="stat-label">Disk Free</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{s.diskUsed != null ? fileService.formatFileSize(s.diskUsed) : '—'}</div>
            <div className="stat-label">Disk Used (OS + Apps)</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{fileService.formatFileSize(s.appUsed)}</div>
            <div className="stat-label">App Storage Used</div>
          </div>
        </div>
      </div>
    );
  }

  private _renderUserList() {
    const users = this._users!;
    return (
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Login</th>
              <th>Storage</th>
              <th>Files</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="user-row" title={u.lastOnline ? `Last online: ${new Date(u.lastOnline).toLocaleString()}` : 'Never online'}>
                <td>
                  <div className="user-cell">
                    {u.picture ? (
                      <img className="user-cell-avatar" src={u.picture} alt={u.displayName} referrerPolicy="no-referrer" />
                    ) : u.emailMd5 ? (
                      <img className="user-cell-avatar" src={`https://www.gravatar.com/avatar/${u.emailMd5}?d=identicon&s=36`} alt={u.displayName} />
                    ) : (
                      <div className="user-cell-avatar-placeholder">
                        {(u.displayName || u.login).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="user-cell-name">
                        {u.displayName || u.login}
                        <span className="user-cell-roles">
                          {u.roles.map(role => (
                            <span key={role} className={`role-badge role-${role}`}>{role}</span>
                          ))}
                        </span>
                      </div>
                      <div className="user-cell-email">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td data-label="Login" className="user-cell-login">
                  <div>{u.login}</div>
                  <div className="user-cell-login-sub">
                    {u.lastOnline
                      ? <><span className="user-cell-login-sub-label">Active </span><Timestamp timestamp={u.lastOnline} short /></>
                      : <span className="user-cell-login-sub-label">Never active</span>}
                  </div>
                </td>
                <td data-label="Storage">
                  <div className="storage-cell">
                    <span>{fileService.formatFileSize(u.usedSpace)}</span>
                    <span className="storage-sep">/</span>
                    <span>{u.storageLimit ? fileService.formatFileSize(u.storageLimit) : '∞'}</span>
                    {u.storageLimit > 0 && (
                      <div className="storage-bar">
                        <div
                          className="storage-bar-fill"
                          style={{ width: `${Math.min(100, (u.usedSpace / u.storageLimit) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                </td>
                <td data-label="Files">
                  {u.filesCount > 0 ? (
                    <Link to={`/?userId=${u.id}`} className="files-count-link">{u.filesCount}</Link>
                  ) : (
                    <span className="text-muted">0</span>
                  )}
                </td>
                <td>
                  <div className="user-actions">
                    <button className="action-btn" title="Storage Overview" onClick={() => this._onStorageOverview(u)}><ImgPieChart /></button>
                    <button className="action-btn" title="Scan Orphans" onClick={() => this._onScanOrphans(u)}><ImgSearch /></button>
                    <button className="action-btn" title="Edit" onClick={() => this._onEditUser(u)}><ImgEdit /></button>
                    {!u.roles.includes('admin') && (
                      <button className="action-btn" title="Delete" onClick={() => this._onDeleteUser(u)}><ImgTrash /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            No users found
          </div>
        )}
      </div>
    );
  }

  @action.bound
  private async _onShowCreateForm() {
    const result = await modalService.show<{}, import('./dialogs/CreateUserDialog.tsx').CreateUserResult>(CreateUserDialog);
    if (result.button !== ModalButtonType.Add || !result.result) return;
    try {
      await apiService.post('/api/user/create', result.result);
      await this._loadUsers();
    } catch (e) {
      runInAction(() => { this._error = `Failed to create user: ${(e as Error).message}`; });
    }
  }

  private _onEditUser = async (user: UserListItem) => {
    const result = await modalService.show<import('./dialogs/EditUserDialog.tsx').EditUserData, import('./dialogs/EditUserDialog.tsx').EditUserResult>(
      EditUserDialog,
      {
        displayName: user.displayName || '',
        email: user.email || '',
        storageLimit: user.storageLimit,
        usedSpace: user.usedSpace,
        roles: user.roles,
      }
    );
    if (result.button !== ModalButtonType.Save || !result.result) return;
    const { displayName, email, storageLimit, password, roles } = result.result;
    const body: Record<string, unknown> = { displayName, email, storageLimit, roles };
    if (password) body.password = password;
    try {
      await apiService.put(`/api/user/${user.id}`, body);
      runInAction(() => { this._error = null; });
      await this._loadUsers();
    } catch (e) {
      runInAction(() => { this._error = `Failed to update user: ${(e as Error).message}`; });
    }
  };

  private _onStorageOverview = async (user: UserListItem) => {
    await modalService.show<import('./dialogs/StorageOverviewDialog.tsx').StorageOverviewData, void>(
      StorageOverviewDialog,
      { userId: user.id, displayName: user.displayName || user.login }
    );
  };

  private _onScanOrphans = async (user: UserListItem) => {
    await modalService.show<import('./dialogs/OrphanScanDialog.tsx').OrphanScanData, void>(
      OrphanScanDialog,
      { userId: user.id, login: user.login, displayName: user.displayName || user.login }
    );
  };

  private _onDeleteUser = (user: UserListItem) => {
    modalService.showConfirmation(
      <span>Are you sure you want to delete user <b>{user.displayName || user.login}</b>?<br />This will delete all their files. This action cannot be undone.</span>,
      'Delete User',
      {
        onDone: async () => {
          try {
            await apiService.delete(`/api/user/${user.id}`);
            await this._loadUsers();
          } catch (e) {
            runInAction(() => { this._error = `Failed to delete user: ${(e as Error).message}`; });
          }
        }
      }
    );
  };
}

export default Users;
